import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Users, Loader2, Crown, ChevronRight, Sparkles } from 'lucide-react';
import { groupsAPI } from '@/services/api';

// Vibrant gradient colors for group cards
const CARD_GRADIENTS = [
  'from-violet-500 to-purple-600',
  'from-pink-500 to-rose-500',
  'from-orange-400 to-amber-500',
  'from-emerald-500 to-teal-500',
  'from-cyan-500 to-blue-500',
  'from-fuchsia-500 to-pink-500',
];

const GroupsPage = () => {
  const navigate = useNavigate();
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchGroups();
  }, []);

  const fetchGroups = async () => {
    try {
      setLoading(true);
      const response = await groupsAPI.list();
      setGroups(response.data);
    } catch (err) {
      setError('Couldn\'t load groups');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
  };

  const getGradient = (index) => {
    return CARD_GRADIENTS[index % CARD_GRADIENTS.length];
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white pb-32">
      {/* Header */}
      <header className="sticky top-0 bg-white z-10 px-6 py-4 flex items-center gap-4 border-b-2 border-black">
        <button 
          onClick={() => navigate('/home')}
          className="hover:opacity-70 transition-opacity"
          data-testid="back-button"
        >
          <ArrowLeft className="w-6 h-6" />
        </button>
        <h1 className="text-xl font-heading font-bold">My Groups</h1>
      </header>

      <main className="p-6 space-y-4">
        {error && (
          <div className="card-base text-center py-6">
            <p className="text-error font-medium mb-4">{error}</p>
            <button onClick={fetchGroups} className="btn-secondary py-2 px-4 text-sm">
              Try Again
            </button>
          </div>
        )}

        {!error && groups.length === 0 ? (
          <div className="card-base text-center py-12">
            <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
              <Users className="w-10 h-10 text-white" />
            </div>
            <h2 className="text-xl font-heading font-bold mb-2">No groups yet</h2>
            <p className="text-neutral-500 mb-6">
              Create a group to split bills with friends, roommates, or trips!
            </p>
            <button
              onClick={() => navigate('/groups/new')}
              className="btn-primary inline-flex items-center gap-2"
              data-testid="create-first-group-button"
            >
              <Plus className="w-5 h-5" />
              Create Your First Group
            </button>
          </div>
        ) : (
          <div className="grid gap-4">
            {groups.map((group, index) => (
              <button
                key={group.id}
                onClick={() => navigate(`/groups/${group.id}`)}
                className="group w-full text-left"
                data-testid={`group-card-${group.id}`}
              >
                <div className={`relative overflow-hidden rounded-2xl border-2 border-black shadow-hard hover:-translate-y-1 hover:shadow-lg transition-all duration-200 bg-gradient-to-br ${getGradient(index)}`}>
                  {/* Decorative sparkles */}
                  <Sparkles className="absolute top-3 right-3 w-5 h-5 text-white/30" />
                  
                  <div className="p-5 text-white">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-12 h-12 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
                        <Users className="w-6 h-6 text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-heading font-bold text-lg truncate">
                          {group.name}
                        </h3>
                        <p className="text-white/80 text-sm">
                          {group.member_count} member{group.member_count !== 1 ? 's' : ''}
                        </p>
                      </div>
                      <ChevronRight className="w-5 h-5 text-white/60 group-hover:translate-x-1 transition-transform" />
                    </div>
                    
                    <div className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-1">
                        {group.is_admin && (
                          <span className="flex items-center gap-1 bg-white/20 px-2 py-0.5 rounded-full text-xs font-medium">
                            <Crown className="w-3 h-3" />
                            Admin
                          </span>
                        )}
                      </div>
                      <span className="text-white/70 text-xs">
                        Created {formatDate(group.created_at)}
                      </span>
                    </div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </main>

      {/* Fixed Bottom CTA */}
      {groups.length > 0 && (
        <div className="fixed bottom-0 left-0 right-0 p-6 bg-white border-t-2 border-black">
          <button
            onClick={() => navigate('/groups/new')}
            className="btn-primary w-full flex items-center justify-center gap-2"
            data-testid="create-group-button"
          >
            <Plus className="w-5 h-5" />
            New Group
          </button>
        </div>
      )}
    </div>
  );
};

export default GroupsPage;
